import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { DOCUMENT_FORM_NAME_DEBT_SCHEDULE } from "@datanac/datanac-api-toolkit";
import { USER_ROLE_ADMIN } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { formatPrice } from "@datanac/datanac-visualization-toolkit/dist/utility/formatters";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { UsersApiHelper, UsersApiHelperForMember } from "api/ApiHelper";
import { AppContext } from "App";
import { MASK_NUMERIC, StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { TilleyDatePicker } from "components/Core/TilleyDatePicker";
import { FinancialIcon } from "components/Icons/Icons";
import { useBlockerPrompt } from "components/Menu/hooks/useBlockerPrompt";
import { getUniqueCropPractices } from "components/Vault/Farm/helpers";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { SkeletonWrapper } from "mui-toolkit/SkeletonWrapper";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { ValueLabel } from "mui-toolkit/ValueLabel";
import { useSnackbar } from "notistack";
import { formatPracticeName } from "utility/formatters";
import { v4 } from "uuid";

import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import EquipmentInventoryLoanEditDialog from "./EquipmentInventoryLoanEditDialog";
import EquipmentInventoryLoansSection from "./EquipmentInventoryLoansSection";
import EquipmentInventoryRepairEditDialog from "./EquipmentInventoryRepairEditDialog";
import EquipmentInventoryRepairsSection from "./EquipmentInventoryRepairsSection";
import { EQUIPMENT_CATEGORIES, EQUIPMENT_CONDITIONS } from "./helpers";

export default function EquipmentInventoryEditWorkspace() {
  const { globalState } = useContext(AppContext);
  const navigate = useNavigate();
  const { equipment_id } = useParams();
  const { state: formStatusState, actions: formStatusActions, component: formStatusComponent } = useFormStatus();
  const { enqueueSnackbar } = useSnackbar();
  const { addAttachments, setOnChangeRecords, clearOnChangeRecords } = useChatAssistantAttachments();

  const isNew = equipment_id === "0";
  const currentYear = globalState?.crop_year;

  const [isLoading, setIsLoading] = useState(true);
  const [equipment, setEquipment] = useState(null);
  const [editingLoan, setEditingLoan] = useState(null);
  const [lien, setLien] = useState(null);
  const [lienHolder, setLienHolder] = useState("");
  const [amountLien, setAmountLien] = useState(0);
  const [countyPractices, setCountyPractices] = useState([]);
  const [isLoadingCountyPractices, setIsLoadingCountyPractices] = useState(false);
  const [countyPracticeItems, setCountyPracticeItems] = useState([]);
  const lastEquipmentIdRef = { current: null };
  const [latestCreatedLoan, setLatestCreatedLoan] = useState(null);
  const [editingRepair, setEditingRepair] = useState(null);
  const [latestCreatedRepair, setLatestCreatedRepair] = useState(null);

  const clearLien = () => {
    setLien("No");
    setAmountLien(0);
    setLienHolder("");
  };
  const checkLienForEquipment = async () => {
    if (!globalState?.producer_token || !currentYear || !equipment?.id) {
      clearLien();
      return;
    }

    try {
      const filter = {
        producer_token: globalState.producer_token,
        year: currentYear,
        is_active: true,
        equipment_inventory_id: equipment.id,
      };

      const debtSchedules = await UsersApiHelperForMember.callPost("debtSchedule/Search", filter, globalState);

      if (debtSchedules && debtSchedules.length > 0) {
        let totalAmount = 0;
        let allLiabilityZero = true;
        const creditors = new Set();

        debtSchedules.forEach((debt) => {
          const liabilityCurrent = Number(debt.liability_current) || 0;
          const liabilityIntermediate = Number(debt.liability_intermediate) || 0;
          const liabilityLongterm = Number(debt.liability_longterm) || 0;

          totalAmount += liabilityCurrent + liabilityIntermediate + liabilityLongterm;
          if (allLiabilityZero && (liabilityCurrent !== 0 || liabilityIntermediate !== 0 || liabilityLongterm !== 0)) {
            allLiabilityZero = false;
          }

          if (debt.creditor) {
            creditors.add(debt.creditor);
          }
        });

        setAmountLien(totalAmount);
        setLien(allLiabilityZero ? "No" : "Yes");
        setLienHolder(Array.from(creditors).join(", ") || "");
      } else {
        clearLien();
      }
    } catch (err) {
      console.warn("Error checking lien:", err);
      clearLien();
    }
  };
  const loadCountyPractices = async () => {
    if (!globalState?.producer_token || !currentYear) return;

    try {
      setIsLoadingCountyPractices(true);

      const producerFilter = {
        producer_token: globalState.producer_token,
        is_active: true,
        crop_year: currentYear,
      };
      const farms = await UsersApiHelper.selectProducerFarmFieldCrops(producerFilter, globalState);

      const _cp = getUniqueCropPractices(farms);

      _cp.forEach((cp) => (cp.is_active = false));

      if (equipment?.id) {
        const equipmentInventoryFilter = {
          producer_token: globalState.producer_token,
          equipment_inventory_id: equipment.id,
          is_active: true,
          year: currentYear,
        };

        const equipmentInventoryCountyPractices = await UsersApiHelper.selectEquipmentInventoryCountyPractices(
          equipmentInventoryFilter,
          globalState
        );

        equipmentInventoryCountyPractices.forEach((cp) => {
          const _index = _cp.findIndex(
            (_cp) => _cp.commodity_name === cp.commodity_name && _cp.practice_name === cp.practice_name
          );
          if (_index > -1) {
            _cp[_index].is_active = true;
          }
        });
      }

      setCountyPractices(_cp);
    } catch (err) {
      console.warn(err);
      setCountyPractices([]);
      enqueueSnackbar(err.detail || err.message || "Error loading county practices", {
        preventDuplicate: true,
        variant: "error",
      });
    } finally {
      setIsLoadingCountyPractices(false);
    }
  };
  const toggleCommodityPracticeState = (currentItem, isChecked) => {
    formStatusActions.setIsFormDirty(true, false);

    const _newList = [...countyPractices];
    const _foundCPIndex = _newList.findIndex(
      (_cp) => _cp.commodity_name === currentItem.commodity_name && _cp.practice_name === currentItem.practice_name
    );

    if (_foundCPIndex > -1) {
      _newList[_foundCPIndex].is_active = isChecked;
      setCountyPractices(_newList);
    }

    const _newState = {
      ...currentItem,
      equipment_inventory_id: equipment?.id,
      producer_token: globalState?.producer_token,
      year: currentYear,
      is_active: isChecked,
    };

    if (!_newState?.id && equipment?.id) {
      _newState.id = v4();
    }

    if (equipment?.id) {
      UsersApiHelper.updateEquipmentInventoryCountyPractices(
        { EquipmentInventoryCountyPractices: [_newState] },
        globalState
      ).catch((err) => {
        console.warn(err);
        enqueueSnackbar(err.detail || err.message || "Error saving", {
          preventDuplicate: true,
          variant: "error",
        });
        if (_foundCPIndex > -1) {
          _newList[_foundCPIndex].is_active = !isChecked;
          setCountyPractices(_newList);
        }
      });
    } else {
      setCountyPracticeItems((prev) => {
        const filtered = prev.filter(
          (item) =>
            !(item.commodity_name === currentItem.commodity_name && item.practice_name === currentItem.practice_name)
        );
        return isChecked ? [...filtered, _newState] : filtered;
      });
    }
  };

  useEffect(() => {
    if (!globalState?.producer_token || !currentYear) return;

    if (isNew) {
      setEquipment({
        id: v4(),
        is_active: true,
        year: currentYear,
      });
      setIsLoading(false);
    } else {
      const loadEquipment = async () => {
        try {
          setIsLoading(true);
          const equipmentList = await UsersApiHelper.selectEquipmentInventory(
            {
              producer_token: globalState.producer_token,
              year: currentYear,
              is_active: true,
              id: equipment_id,
            },
            globalState
          );
          const foundEquipment = Array.isArray(equipmentList)
            ? equipmentList.find((eq) => eq.id === equipment_id) || null
            : null;
          setEquipment(foundEquipment);
        } catch (err) {
          console.warn(err);
          enqueueSnackbar(err.detail || err.message || "Error loading equipment", {
            preventDuplicate: true,
            variant: "error",
          });
        } finally {
          setIsLoading(false);
        }
      };

      loadEquipment();
    }
  }, [globalState?.producer_token, currentYear, equipment_id, isNew]);

  useEffect(() => {
    if (equipment?.id && globalState?.producer_token && currentYear) {
      if (lastEquipmentIdRef.current !== equipment.id) {
        lastEquipmentIdRef.current = equipment.id;
        loadCountyPractices();
      }
    }
  }, [equipment?.id, globalState?.producer_token, currentYear]);

  useEffect(() => {
    if (equipment?.id && currentYear && globalState?.producer_token) {
      checkLienForEquipment();
    }
  }, [equipment?.id, currentYear, globalState?.producer_token]);

  const gainOrLossValue = useMemo(() => {
    const appraisedValue = Number(equipment?.appraised_value) || 0;
    const _amountLien = Number(amountLien) || 0;
    return appraisedValue - _amountLien;
  }, [equipment?.appraised_value, amountLien]);

  const handleChangeState = (event, value) => {
    const eventName = event.target.name;
    let newValue = value ?? event.target.value;

    const numericFields = ["value", "adjusted_value", "prior_value", "appraised_value"];
    if (numericFields.includes(eventName) && newValue === "") {
      newValue = null;
    }

    if (equipment[eventName] !== newValue) {
      formStatusActions.setIsFormDirty(true, false);
      setEquipment((prev) => ({ ...prev, [eventName]: newValue }));
    }
  };

  const handleChangeStateSelect = (event) => {
    if (equipment[event.target.name] !== event.target.value) {
      formStatusActions.setIsFormDirty(true, false);
      setEquipment((prev) => ({ ...prev, [event.target.name]: event.target.value }));
    }
  };

  const isValid = () => {
    return equipment?.id && equipment?.name && equipment?.name.trim() !== "";
  };

  useBlockerPrompt(!isValid(), "EquipmentInventory");

  useEffect(() => {
    if (isValid() && globalState?.producer_token && formStatusState?.isFormDirty) {
      formStatusActions.setIsSaving(true);

      const saveEquipment = async () => {
        try {
          const equipmentToSave = { ...equipment, year: currentYear };
          await UsersApiHelper.updateEquipmentInventory({ EquipmentInventory: [equipmentToSave] }, globalState);

          if (equipment_id === "0" && countyPracticeItems.length > 0) {
            const itemsToSave = countyPracticeItems.map((item) => ({
              ...item,
              equipment_inventory_id: equipmentToSave.id,
            }));

            await UsersApiHelper.updateEquipmentInventoryCountyPractices(
              { EquipmentInventoryCountyPractices: itemsToSave },
              globalState
            );
          }
          formStatusActions.setIsSaving(false);
          formStatusActions.setIsFormDirty(false);

          if (equipment_id === "0") {
            navigate(`/financial/equipment/${equipment?.id}`, { replace: true });
          }
        } catch (err) {
          formStatusActions.setIsFormDirty(true, false);
          formStatusActions.setIsSaving(false);
          enqueueSnackbar(err.detail || err.message || JSON.stringify(err), {
            preventDuplicate: true,
            variant: "error",
          });
        }
      };

      saveEquipment();
    }
  }, [
    currentYear,
    globalState?.producer_token,
    formStatusState?.isFormDirty,
    equipment_id,
    equipment?.name,
    countyPracticeItems,
  ]);
  const sortedCountyPractices = useMemo(() => {
    return [...countyPractices].sort((a, b) => {
      const aName = a.commodity_name || "";
      const bName = b.commodity_name || "";
      return aName.localeCompare(bName);
    });
  }, [countyPractices]);

  const handleClickNewLoan = () => {
    setEditingLoan({
      equipment_inventory_id: equipment?.id,
      is_active: true,
      year: currentYear,
    });
  };

  const handleUpdateLoan = async (loan) => {
    try {
      const effectiveYear = loan?.year || currentYear;
      const _documentsFilter = {
        year: effectiveYear,
        document_type_name: DOCUMENT_FORM_NAME_DEBT_SCHEDULE,
        is_active: true,
      };
      const _documents = await UsersApiHelper.users_selectObject("FinancialDocument", _documentsFilter, globalState);

      const _documentID = _documents?.length > 0 ? _documents[0].id : v4();

      if (!_documents?.length) {
        await UsersApiHelper.updateFinancialDocument(
          {
            id: _documentID,
            year: effectiveYear,
            document_type_name: DOCUMENT_FORM_NAME_DEBT_SCHEDULE,
            is_active: true,
          },
          globalState
        );
      }

      const loanToSave = {
        ...loan,
        id: loan.id || v4(),
        document_id: _documentID,
        producer_token: globalState.producer_token,
        year: effectiveYear,
        equipment_inventory_id: equipment.id,
      };

      await UsersApiHelperForMember.callPost(
        "debtSchedule",
        {
          DebtSchedule: [loanToSave],
          document_id: loanToSave.document_id,
          producer_token: globalState.producer_token,
        },
        globalState
      );

      const savedLoan = { ...loanToSave };
      setLatestCreatedLoan(savedLoan);
      checkLienForEquipment();
    } catch (err) {
      console.warn("Error saving loan:", err);
      enqueueSnackbar(err.detail || err.message || "Error saving loan", {
        variant: "error",
        preventDuplicate: true,
      });
    }
  };

  const handleClickNewRepair = () => {
    setEditingRepair({
      equipment_inventory_id: equipment?.id,
      is_active: true,
      year: currentYear,
    });
  };

  const handleUpdateRepair = async (repair) => {
    try {
      const parts = Number(repair?.parts) || 0;
      const labor = Number(repair?.labor) || 0;

      const repairToSave = {
        equipment_inventory_id: equipment.id,
        id: repair.id || v4(),
        repair_date: repair.repair_date,
        description: repair.description,
        vendor: repair.vendor || "",
        parts: parts,
        labor: labor,
        category: repair.category || "Repair",
        is_active: repair.is_active !== false,
        receipt_image_url: repair.receipt_image_url,
        receipt_image_name: repair.receipt_image_name,
      };

      await UsersApiHelperForMember.callPost(
        "financial/EquipmentInventoryRepair",
        {
          EquipmentInventoryRepair: [repairToSave],
          producer_token: globalState.producer_token,
        },
        globalState
      );

      const savedRepair = { ...repairToSave };
      setLatestCreatedRepair(savedRepair);
    } catch (err) {
      console.warn("Error saving repair:", err);
      enqueueSnackbar(err.detail || err.message || "Error saving repair", {
        variant: "error",
        preventDuplicate: true,
      });
    }
  };

  const handleOnChangeRecords = useCallback(
    async (records) => {
      if (records && records.length > 0) {
        const updatedEquipment = records[0];
        if (updatedEquipment && equipment) {
          const mergedEquipment = {
            ...equipment,
            ...updatedEquipment,
            id: equipment.id,
            year: currentYear,
          };
          setEquipment(mergedEquipment);
          formStatusActions.setIsFormDirty(true, false);
        }
      }
    },
    [equipment, currentYear]
  );
  useEffect(() => {
    if (equipment) {
      addAttachments([{ name: "Equipment", content: equipment }]);
    }
  }, [equipment, addAttachments]);

  useEffect(() => {
    setOnChangeRecords(handleOnChangeRecords);
    return () => {
      clearOnChangeRecords();
    };
  }, [setOnChangeRecords, clearOnChangeRecords, handleOnChangeRecords]);

  const isLender = globalState?.is_lender === true;
  const isAdmin = globalState?.user_role === USER_ROLE_ADMIN;

  return (
    <>
      <WorkspaceHeader
        title={isNew ? "New Equipment" : equipment?.name || "Edit Equipment"}
        category={{
          title: "Financial Analysis",
          icon: <FinancialIcon />,
        }}
        breadCrumbs={[
          {
            title: "Equipment Inventory",
            path: "/financial/equipment",
          },
        ]}
      />

      <Box sx={{ width: "100%" }}>
        <WorkspacePageHeader title="Equipment details" additionalControls={formStatusComponent} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Description"
                name="name"
                isRequired={true}
                defaultValue={equipment?.name || ""}
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Serial Number"
                name="serial_number"
                isRequired={false}
                defaultValue={equipment?.serial_number || ""}
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <FormControl
              fullWidth={true}
              sx={{
                "@media (max-width: 899.95px)": {
                  marginBottom: "20px",
                },
              }}
            >
              <SkeletonWrapper variant="textfield" sx={{ marginBottom: "20px" }} isLoading={isLoading}>
                <InputLabel id="categoryLabel">Classification</InputLabel>
                <Select
                  labelId="categoryLabel"
                  value={equipment?.category || ""}
                  name="category"
                  onChange={handleChangeStateSelect}
                  label="Classification"
                >
                  {EQUIPMENT_CATEGORIES?.map((currentItem, index) => {
                    return (
                      <MenuItem key={index} value={currentItem}>
                        {currentItem}
                      </MenuItem>
                    );
                  })}
                </Select>
              </SkeletonWrapper>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Use"
                name="purpose"
                isRequired={false}
                defaultValue={equipment?.purpose || ""}
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <FormControl
              fullWidth={true}
              sx={{
                "@media (max-width: 899.95px)": {
                  marginBottom: "20px",
                },
              }}
            >
              <SkeletonWrapper variant="textfield" sx={{ marginBottom: "20px" }} isLoading={isLoading}>
                <InputLabel id="conditionLabel">Condition</InputLabel>
                <Select
                  labelId="conditionLabel"
                  value={equipment?.condition || ""}
                  name="condition"
                  onChange={handleChangeStateSelect}
                  label="Condition"
                >
                  {EQUIPMENT_CONDITIONS?.map((currentItem, index) => {
                    return (
                      <MenuItem key={index} value={currentItem}>
                        {currentItem}
                      </MenuItem>
                    );
                  })}
                </Select>
              </SkeletonWrapper>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Value $"
                name="value"
                isRequired={false}
                defaultValue={equipment?.value ?? ""}
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
                disabled={isLender && !isAdmin}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>

          {(isLender || isAdmin) && (
            <Grid item xs={12} md={4} lg={3}>
              <FormControl fullWidth={true}>
                <StatefulControlledTextField
                  label="Adjusted Value $"
                  name="adjusted_value"
                  isRequired={false}
                  defaultValue={equipment?.adjusted_value ?? ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                  isLoading={isLoading}
                />
              </FormControl>
            </Grid>
          )}

          {isLender && (
            <Grid item xs={12} md={4} lg={3}>
              <FormControl fullWidth={true}>
                <StatefulControlledTextField
                  label="Prior Value $"
                  name="prior_value"
                  isRequired={false}
                  defaultValue={equipment?.prior_value ?? ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                  isLoading={isLoading}
                />
              </FormControl>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Appraiser"
                name="appraiser_name"
                isRequired={false}
                defaultValue={equipment?.appraiser_name || ""}
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Appraised Value $"
                name="appraised_value"
                isRequired={false}
                defaultValue={equipment?.appraised_value ?? ""}
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <SkeletonWrapper variant="textfield" sx={{ marginBottom: "20px" }} isLoading={isLoading}>
              <TilleyDatePicker
                name="appraised_date"
                label="Appraisal Date"
                value={equipment?.appraised_date}
                onChange={handleChangeState}
              />
            </SkeletonWrapper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={2}>
          {isLoadingCountyPractices ? (
            <>
              {[...Array(3)].map((_, index) => (
                <Grid item xs={12} md={6} lg={3} key={index}>
                  <SkeletonWrapper variant="textfield" isLoading={true}>
                    <Box sx={{ height: 40 }} />
                  </SkeletonWrapper>
                </Grid>
              ))}
            </>
          ) : (
            sortedCountyPractices?.map((currentItem, index) => (
              <Grid item xs={12} md={6} lg={3} key={index}>
                <FormControl fullWidth={true}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(currentItem?.is_active)}
                        onChange={(e) => toggleCommodityPracticeState(currentItem, e?.target?.checked)}
                      />
                    }
                    label={`${currentItem?.commodity_name} ${formatPracticeName(currentItem?.practice_name)}`}
                  />
                </FormControl>
              </Grid>
            ))
          )}

          {!isLoadingCountyPractices && !countyPractices?.length && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                No available practices to select
              </Typography>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Grid
          item
          xs={12}
          sx={{
            pb: 2,
            mb: 2,
          }}
        >
          <Box className="flexWrapContainer">
            <ValueLabel label="Lien" value={lien || "-"} />
            <ValueLabel label="Amount of Lien" value={formatPrice(amountLien, 0) || "-"} />
            <ValueLabel label="Lien Holder" value={lienHolder || "-"} />
            <ValueLabel label="Gain or Loss $" value={formatPrice(gainOrLossValue, 0) || "-"} />
          </Box>
        </Grid>

        <EquipmentInventoryRepairsSection
          equipmentId={equipment?.id}
          onRepairCreated={latestCreatedRepair}
          onEditRepair={(repair) => {
            setEditingRepair(repair);
          }}
        />
        <EquipmentInventoryLoansSection
          equipmentId={equipment?.id}
          onDeleteLoan={() => {
            checkLienForEquipment();
          }}
          onLoanCreated={latestCreatedLoan}
        />
      </Box>
      <WorkspaceActionBar
        content={
          <>
            <Button variant="contained" onClick={handleClickNewLoan} startIcon={<AddCircleOutline />}>
              Add Loan
            </Button>
            <Button variant="contained" onClick={handleClickNewRepair} startIcon={<AddCircleOutline />}>
              Add Repair
            </Button>
          </>
        }
      />
      <Dialog open={Boolean(editingLoan)} onClose={() => setEditingLoan(null)}>
        <EquipmentInventoryLoanEditDialog
          loan={editingLoan}
          onChange={handleUpdateLoan}
          onClose={() => {
            setEditingLoan(null);
            checkLienForEquipment();
          }}
        />
      </Dialog>
      <Dialog open={Boolean(editingRepair)} onClose={() => setEditingRepair(null)}>
        <EquipmentInventoryRepairEditDialog
          repair={editingRepair}
          onChange={handleUpdateRepair}
          onClose={() => {
            setEditingRepair(null);
          }}
        />
      </Dialog>
    </>
  );
}
